import { useCallback, useEffect, useRef, useState } from 'react';
import { clearMarketDataCache, requestMarketCandles } from '../api/marketDataClient';
import { strategyClient } from '../lib/strategyClient';
import type { MarketInterval, MarketRange, SymbolSnapshot } from '../types/market';

export function useMarketDashboard(
  symbols: string[],
  interval: MarketInterval,
  range: MarketRange,
  extendedHours: boolean,
  enabled = true,
  pollMs = 15_000
) {
  const [snapshots, setSnapshots] = useState<Record<string, SymbolSnapshot>>({});
  const generationRef = useRef(0);

  const refreshSymbol = useCallback(async (symbol: string, generation = generationRef.current) => {
    if (!enabled) return;

    setSnapshots((current) => ({
      ...current,
      [symbol]: {
        ...current[symbol],
        symbol,
        loading: true,
        error: undefined
      }
    }));

    try {
      const data = await requestMarketCandles({ symbol, interval, range, extendedHours });
      if (generation !== generationRef.current) return;

      setSnapshots((current) => ({
        ...current,
        [symbol]: {
          ...current[symbol],
          symbol,
          data,
          loading: false,
          error: undefined,
          analysisLoading: true,
          analysisError: undefined,
          updatedAt: Date.now()
        }
      }));

      try {
        const analysis = await strategyClient.analyze(data.candles);
        if (generation !== generationRef.current) return;
        setSnapshots((current) => ({
          ...current,
          [symbol]: {
            ...current[symbol],
            symbol,
            data,
            analysis,
            loading: false,
            analysisLoading: false,
            analysisError: undefined,
            updatedAt: Date.now()
          }
        }));
      } catch (analysisError) {
        if (generation !== generationRef.current) return;
        setSnapshots((current) => ({
          ...current,
          [symbol]: {
            ...current[symbol],
            symbol,
            data,
            loading: false,
            analysisLoading: false,
            analysisError: analysisError instanceof Error ? analysisError.message : String(analysisError),
            updatedAt: Date.now()
          }
        }));
      }
    } catch (error) {
      if (generation !== generationRef.current) return;
      setSnapshots((current) => ({
        ...current,
        [symbol]: {
          ...current[symbol],
          symbol,
          loading: false,
          analysisLoading: false,
          error: error instanceof Error ? error.message : String(error)
        }
      }));
    }
  }, [enabled, interval, range, extendedHours]);

  const refreshAll = useCallback(async (force = false) => {
    if (!enabled) return;
    if (force) clearMarketDataCache();
    const generation = generationRef.current;
    await Promise.allSettled(symbols.map((symbol) => refreshSymbol(symbol, generation)));
  }, [enabled, refreshSymbol, symbols]);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;

    if (!enabled) {
      setSnapshots((current) => {
        const next: Record<string, SymbolSnapshot> = {};
        for (const symbol of symbols) next[symbol] = current[symbol] ?? { symbol, loading: false };
        return next;
      });
      return;
    }

    setSnapshots((current) => {
      const next: Record<string, SymbolSnapshot> = {};
      for (const symbol of symbols) next[symbol] = current[symbol] ?? { symbol, loading: true };
      return next;
    });

    void Promise.allSettled(symbols.map((symbol) => refreshSymbol(symbol, generation)));
    const timer = window.setInterval(() => {
      void Promise.allSettled(symbols.map((symbol) => refreshSymbol(symbol, generation)));
    }, Math.max(5_000, pollMs));

    return () => window.clearInterval(timer);
  }, [enabled, interval, pollMs, range, extendedHours, refreshSymbol, symbols]);

  return { snapshots, refreshAll, refreshSymbol, pollMs };
}
